import React, { useCallback, useEffect, useState } from "react";
import "./retractable-floating-launcher.css";

export type RetractableFloatingVariant = "whatsapp" | "tec";

type Props = {
  variant: RetractableFloatingVariant;
  ariaLabelExpand: string;
  ariaLabelAction: string;
  ariaLabelRetract: string;
  onAction: () => void;
  children: React.ReactNode;
  /** Burbuja de diálogo opcional junto al botón (ej. "¿Te sientes perdido? Hablemos"). */
  bubble?: React.ReactNode;
};

const BUBBLE_DELAY_MS = 2500;
/** En celular la burbuja tapa contenido: se oculta sola pasado este tiempo. */
const BUBBLE_MOBILE_MS = 7000;

const RetractableFloatingLauncher: React.FC<Props> = ({
  variant,
  ariaLabelExpand,
  ariaLabelAction,
  ariaLabelRetract,
  onAction,
  children,
  bubble,
}) => {
  const [retracted, setRetracted] = useState(false);
  const [bubbleVisible, setBubbleVisible] = useState(false);
  const [bubbleClosed, setBubbleClosed] = useState(false);

  useEffect(() => {
    if (!bubble) return;
    const show = window.setTimeout(() => setBubbleVisible(true), BUBBLE_DELAY_MS);
    const hide = window.matchMedia("(max-width: 640px)").matches
      ? window.setTimeout(() => setBubbleClosed(true), BUBBLE_DELAY_MS + BUBBLE_MOBILE_MS)
      : undefined;
    return () => {
      window.clearTimeout(show);
      window.clearTimeout(hide);
    };
  }, [bubble]);

  const showBubble = Boolean(bubble) && bubbleVisible && !bubbleClosed && !retracted;

  const handleChevronClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setRetracted((value) => !value);
    },
    []
  );

  const handleBoxClick = useCallback(() => {
    if (retracted) {
      setRetracted(false);
      return;
    }
    onAction();
  }, [retracted, onAction]);

  return (
    <div
      className={`rf-launcher rf-launcher--${variant} ${retracted ? "rf-launcher--retracted" : "rf-launcher--expanded"}`}
    >
      {showBubble && (
        <div className="rf-bubble" role="status">
          <button type="button" className="rf-bubble__text" onClick={onAction}>
            {bubble}
          </button>
          <button
            type="button"
            className="rf-bubble__close"
            onClick={() => setBubbleClosed(true)}
            aria-label="Cerrar mensaje"
          >
            <svg viewBox="0 0 12 12" aria-hidden="true">
              <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}
      <div className="rf-launcher__core">
        <button
          type="button"
          className="rf-launcher__box"
          onClick={handleBoxClick}
          aria-label={retracted ? ariaLabelExpand : ariaLabelAction}
        >
          {children}
        </button>
        <button
          type="button"
          className="rf-launcher__chevron"
          onClick={handleChevronClick}
          aria-label={retracted ? ariaLabelExpand : ariaLabelRetract}
          aria-expanded={!retracted}
        >
          <span className="rf-launcher__chevron-icon" aria-hidden="true">
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10.5 3.5L5.5 8L10.5 12.5"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>
      </div>
    </div>
  );
};

export default RetractableFloatingLauncher;
